import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Shared stacking + placement rules for every dropdown panel in the app.
 *
 * WHY THIS MODULE EXISTS
 * The seven shared select components each picked their own stacking value:
 * SearchableSelect used z-[9999], five others used z-50, and CustomSelect had
 * none at all. Modals in this app sit at z-[9999] (components/modals/AddUser.jsx,
 * AddAdmin.jsx), so a z-50 panel opened INSIDE a modal painted underneath the
 * modal body — the "fields merging" bug reported on Add Doctor (country list),
 * View Appointment (Location list) and Login (hospital list over Email/Password).
 * One constant imported by all seven is what stops component number eight from
 * inventing a ninth number.
 */

// Above the z-[9999] modal layer, because a dropdown opened inside a modal must
// float over the modal body. Applied as an inline style rather than a class for
// two reasons: the portaled panel already carries computed coordinates inline,
// and an inline z-index deterministically beats a caller's stray class (e.g.
// components/doctorTabs/AvailabilityTab.jsx passes dropdownClassName="z-[9999]").
export const DROPDOWN_Z_INDEX = 10000;

// Hard ceiling for a panel, so a 200-country list can never grow taller than the
// viewport. Panels scroll internally instead.
export const DROPDOWN_MAX_HEIGHT = 320;

// Never let a panel touch the viewport edge...
const VIEWPORT_MARGIN = 8;
// ...and keep a small gap between trigger and panel (replaces the old mt-1/mt-2,
// which no longer applies once the panel is position:fixed).
const TRIGGER_GAP = 4;
// Below this a panel is too short to be usable; we would rather let it run a
// little past the viewport edge than render a 30px sliver of a list.
const MIN_PANEL_HEIGHT = 120;

/**
 * Positions a dropdown panel that is portaled to document.body.
 *
 * WHY PORTAL AT ALL — a z-index only competes inside its own stacking context.
 * These selects sit in filter bars with overflow-x-auto and in modals whose
 * wrappers use transform/opacity, both of which trap and clip a child panel no
 * matter how high its z-index. Rendering to document.body with position:fixed is
 * what makes DROPDOWN_Z_INDEX mean anything, and is the same escape hatch
 * components/layout/Sidebar.jsx uses for its tooltips.
 *
 * @param isOpen  mount state of the panel — pass the exact condition used to
 *                render it, so measurement only runs while it is on screen.
 * @param options.matchTriggerWidth  false for panels with their own fixed width
 *                (PhoneCountryDropdown is w-64 beside a tiny flag button).
 * @param options.maxHeight          override the ceiling above.
 * @returns anchorRef  attach to the trigger button (the panel is measured from it)
 *          panelRef   attach to the panel (also needed for outside-click checks,
 *                     since a portaled panel is no longer inside the container)
 *          panelStyle inline style for the panel. Which way it opened is encoded
 *                     in there: a `top` offset means downward, `bottom` upward.
 */
export function useAnchoredDropdown(isOpen, options = {}) {
    const {
        matchTriggerWidth = true,
        maxHeight = DROPDOWN_MAX_HEIGHT,
        gap = TRIGGER_GAP,
    } = options;

    const anchorRef = useRef(null);
    const panelRef = useRef(null);
    const [panelStyle, setPanelStyle] = useState(null);

    // The panel's natural height, measured once per open. Re-measuring it while a
    // max-height is applied would feed the SHRUNKEN height back into the flip test
    // ("it fits below now!"), so the panel would oscillate between above and below
    // on every scroll event.
    const naturalHeightRef = useRef(0);
    // Skips the setState when a scroll produced identical geometry — the scroll
    // listener is capturing and fires for every ancestor.
    const lastKeyRef = useRef('');

    const measure = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor) return;

        const rect = anchor.getBoundingClientRect();
        const panel = panelRef.current;
        const wanted = naturalHeightRef.current || maxHeight;

        const spaceBelow = window.innerHeight - rect.bottom - gap - VIEWPORT_MARGIN;
        const spaceAbove = rect.top - gap - VIEWPORT_MARGIN;

        // Flip up only when the panel genuinely does not fit below AND there is
        // more room above — otherwise a control halfway down the page would flip
        // for no reason. This is the "Items per page" case at the bottom of a
        // list, which used to open over the cards it belongs to.
        const openUp = spaceBelow < wanted && spaceAbove > spaceBelow;
        const available = Math.max(
            MIN_PANEL_HEIGHT,
            Math.min(maxHeight, openUp ? spaceAbove : spaceBelow)
        );

        // Clamp horizontally using the panel's real width: a min-w- class can make
        // it wider than its trigger, and a right-aligned trigger would then push
        // the panel off screen.
        const width = panel?.offsetWidth || rect.width;
        const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
        const left = Math.min(Math.max(rect.left, VIEWPORT_MARGIN), maxLeft);

        // Anchor the panel's BOTTOM edge to the trigger's top when opening upward,
        // so its height does not have to be known to place it.
        const offset = openUp
            ? Math.round(window.innerHeight - rect.top + gap)
            : Math.round(rect.bottom + gap);

        const key = `${openUp}|${Math.round(left)}|${offset}|${Math.round(rect.width)}|${Math.round(available)}`;
        if (key === lastKeyRef.current) return;
        lastKeyRef.current = key;

        setPanelStyle({
            position: 'fixed',
            left: `${Math.round(left)}px`,
            [openUp ? 'bottom' : 'top']: `${offset}px`,
            ...(matchTriggerWidth ? { width: `${Math.round(rect.width)}px` } : null),
            maxHeight: `${Math.round(available)}px`,
            zIndex: DROPDOWN_Z_INDEX,
        });
    }, [gap, matchTriggerWidth, maxHeight]);

    // Pass 1: place the panel from the trigger rect, and keep it attached while
    // any ancestor scrolls (capture phase — a modal body scrolls, not the window).
    useLayoutEffect(() => {
        if (!isOpen) {
            naturalHeightRef.current = 0;
            lastKeyRef.current = '';
            setPanelStyle(null);
            return undefined;
        }

        measure();
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return () => {
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
    }, [isOpen, measure]);

    // Pass 2, still before paint: now that the panel has been laid out at its real
    // width, swap the assumed ceiling for its actual height, so a three-row menu
    // does not flip upward just because a twenty-row one would not have fitted.
    // Guarded on panelStyle because measuring the panel before pass 1 commits
    // would read it at auto width, wrapping the labels and lying about the height.
    useLayoutEffect(() => {
        if (!isOpen || !panelStyle || naturalHeightRef.current || !panelRef.current) return;
        naturalHeightRef.current = Math.min(panelRef.current.scrollHeight, maxHeight);
        measure();
    }, [isOpen, panelStyle, measure, maxHeight]);

    return {
        anchorRef,
        panelRef,
        // Before the first measurement the panel is mounted but unplaced; render
        // it hidden at the ceiling height so it can be measured without ever being
        // painted in the top-left corner.
        panelStyle: panelStyle || {
            position: 'fixed',
            top: 0,
            left: 0,
            maxHeight: `${maxHeight}px`,
            visibility: 'hidden',
            zIndex: DROPDOWN_Z_INDEX,
        },
    };
}
